/**
 * AgreementManagementPage — `/franchise/agreements`.
 *
 * Lists agreements per-franchise (the backend exposes
 * `GET /v1/franchise/agreements/franchise/:franchiseId`).  Inline lifecycle:
 * create DRAFT, sign (DRAFT → ACTIVE), counter-sign (NIPOST acknowledgement),
 * attach a signed PDF object-store URL.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  counterSignAgreement,
  createAgreement,
  listAgreementsForFranchise,
  listFranchises,
  setAgreementPdfUrl,
  signAgreement,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export function AgreementManagementPage(): JSX.Element {
  const qc = useQueryClient();
  const franchises = useQuery({
    queryKey: ['franchise', 'agreements', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const [franchiseId, setFranchiseId] = useState('');
  const [actionErr, setActionErr] = useState<string | null>(null);

  const agreements = useQuery({
    queryKey: ['franchise', 'agreements', 'list', franchiseId],
    queryFn: () => listAgreementsForFranchise(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const sign = useMutation({
    mutationFn: (id: string) => signAgreement(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'agreements', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const counterSign = useMutation({
    mutationFn: (id: string) => counterSignAgreement(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'agreements', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const attachPdf = useMutation({
    mutationFn: (id: string) => {
      const pdfUrl = window.prompt(
        'Object-store URL for the signed PDF (https://…)?',
      ) ?? '';
      if (pdfUrl.length === 0) throw new Error('Cancelled');
      return setAgreementPdfUrl(id, pdfUrl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'agreements', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const create = useMutation({
    mutationFn: () => {
      const now = new Date();
      const until = new Date(now.getTime() + ONE_YEAR_MS);
      return createAgreement({
        franchiseId,
        terms: { type: 'standard', revShare: '0.05' },
        effectiveFrom: now.toISOString(),
        effectiveUntil: until.toISOString(),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'agreements', 'list', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const denied = useMemo(
    () =>
      (franchises.isError && franchises.error instanceof ApiError &&
        (franchises.error.status === 401 || franchises.error.status === 403)) ||
      (agreements.isError && agreements.error instanceof ApiError &&
        (agreements.error.status === 401 || agreements.error.status === 403)),
    [franchises, agreements],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agreement management"
        description="Franchise agreements — DRAFT / ACTIVE / EXPIRED / TERMINATED."
      />

      {denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">FranchiseAgreement/read</code>.
        </DeferredNotice>
      ) : null}

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-muted">Franchise</span>
        {franchises.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <select
            value={franchiseId}
            onChange={(e) => setFranchiseId(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm flex-1 min-w-[280px]"
          >
            <option value="">Select…</option>
            {franchises.data?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.legalName} ({f.franchiseNumber})
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => {
            setActionErr(null);
            create.mutate();
          }}
          disabled={franchiseId.length === 0 || create.isPending}
          className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'New agreement'}
        </button>
      </div>

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to view its agreements.
        </p>
      ) : agreements.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : agreements.isError ? (
        <DeferredNotice title="Service unreachable">{(agreements.error as Error).message}</DeferredNotice>
      ) : (agreements.data?.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No agreements yet — click <em>New agreement</em> to draft one.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Number</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Effective</th>
                <th className="text-left px-4 py-2">Until</th>
                <th className="text-left px-4 py-2">Signed</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {agreements.data?.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{a.agreementNumber}</td>
                  <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(a.effectiveFrom).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(a.effectiveUntil).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {a.signedAt ? new Date(a.signedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right space-x-3">
                    {a.status === 'DRAFT' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          sign.mutate(a.id);
                        }}
                        disabled={sign.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Sign →
                      </button>
                    ) : null}
                    {a.status === 'ACTIVE' && !a.counterSignedAt ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          counterSign.mutate(a.id);
                        }}
                        disabled={counterSign.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                        data-testid="agreement-countersign-btn"
                      >
                        Counter-sign
                      </button>
                    ) : null}
                    {a.counterSignedAt ? (
                      <span className="text-xs text-muted" data-testid="agreement-countersigned">
                        Counter-signed {new Date(a.counterSignedAt).toLocaleDateString()}
                      </span>
                    ) : null}
                    {a.status === 'ACTIVE' || a.status === 'DRAFT' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          attachPdf.mutate(a.id);
                        }}
                        disabled={attachPdf.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                        data-testid="agreement-pdf-btn"
                      >
                        {a.pdfUrl ? 'Replace PDF' : 'Attach PDF'}
                      </button>
                    ) : null}
                    {a.pdfUrl ? (
                      <a
                        href={a.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-nipost-green hover:underline"
                      >
                        PDF ↗
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
    </div>
  );
}
